/**
 * Utility functions for generating unique data-testid attributes for screen editor
 *
 * Naming convention:
 * - Section: `section-{sectionName}` (e.g., "section-header", "section-footer")
 * - Field: `field-{sectionName}-{fieldName}` (e.g., "field-header-backButton", "field-footer-button1")
 * - Styles Panel: `styles-{sectionName}-{fieldName}` (e.g., "styles-header-backButton")
 * - Button Styles Panel: `button-styles-{sectionName}-{fieldName}`
 * - Style Field: `style-field-{fieldName}-{styleName}` (e.g., "style-field-backButton-fontSize")
 * - Action Button: `btn-{action}` (e.g., "btn-save", "btn-close")
 *
 * All names are converted to kebab-case for consistency
 */

/**
 * Convert string to kebab-case
 * Examples:
 *   "Back Button" -> "back-button"
 *   "button1" -> "button1"
 *   "Page Indicator" -> "page-indicator"
 */
const toKebabCase = (str: string): string => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2') // camelCase to kebab-case
    .replace(/[\s_]+/g, '-') // spaces and underscores to hyphens
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, ''); // remove special characters
};

/**
 * Generate test ID for a section accordion
 * @param sectionName - Section name (e.g., "Header", "Footer", "Background")
 * @returns data-testid value (e.g., "section-header")
 */
export const getSectionTestId = (sectionName: string): string => {
  return `section-${toKebabCase(sectionName)}`;
};

/**
 * Generate test ID for a field element
 * @param sectionName - Section name (e.g., "Header", "Footer")
 * @param fieldName - Field name from FieldConfig.label or custom name (e.g., "Back Button", "button1")
 * @returns data-testid value (e.g., "field-header-back-button")
 */
export const getFieldTestId = (sectionName: string, fieldName: string): string => {
  return `field-${toKebabCase(sectionName)}-${toKebabCase(fieldName)}`;
};

/**
 * Generate test ID for a styles panel toggle button (gear icon)
 * @param sectionName - Section name
 * @param fieldName - Field name
 * @returns data-testid value (e.g., "styles-toggle-header-back-button")
 */
export const getStylesToggleTestId = (sectionName: string, fieldName: string): string => {
  return `styles-toggle-${toKebabCase(sectionName)}-${toKebabCase(fieldName)}`;
};

/**
 * Generate test ID for a button styles panel toggle button
 * @param sectionName - Section name
 * @param fieldName - Field name
 * @returns data-testid value (e.g., "button-styles-toggle-footer-button1")
 */
export const getButtonStylesToggleTestId = (sectionName: string, fieldName: string): string => {
  return `button-styles-toggle-${toKebabCase(sectionName)}-${toKebabCase(fieldName)}`;
};

/**
 * Generate test ID for a style field input
 * @param fieldName - Parent field name
 * @param styleName - Style property name (e.g., "fontSize", "color", "fontFamily")
 * @returns data-testid value (e.g., "style-field-back-button-font-size")
 */
export const getStyleFieldTestId = (fieldName: string, styleName: string): string => {
  return `style-field-${toKebabCase(fieldName)}-${toKebabCase(styleName)}`;
};

/**
 * Generate test ID for a button style field input
 * @param fieldName - Parent field name
 * @param styleName - Style property name (e.g., "backgroundColor", "borderWidth")
 * @returns data-testid value (e.g., "button-style-field-button1-background-color")
 */
export const getButtonStyleFieldTestId = (fieldName: string, styleName: string): string => {
  return `button-style-field-${toKebabCase(fieldName)}-${toKebabCase(styleName)}`;
};

/**
 * Generate test ID for action buttons (Save, Close, etc.)
 * @param action - Action name (e.g., "save", "close", "cancel")
 * @returns data-testid value (e.g., "btn-save")
 */
export const getActionButtonTestId = (action: string): string => {
  return `btn-${toKebabCase(action)}`;
};

/**
 * Generate test ID for enable/disable checkbox
 * @param sectionName - Section name
 * @param fieldName - Field name
 * @returns data-testid value (e.g., "checkbox-header-back-button")
 */
export const getCheckboxTestId = (sectionName: string, fieldName: string): string => {
  return `checkbox-${toKebabCase(sectionName)}-${toKebabCase(fieldName)}`;
};

/**
 * Generate test ID for padding fields
 * @param sectionName - Section name
 * @param fieldName - Field name
 * @param side - Padding side (top, left, right, bottom)
 * @returns data-testid value (e.g., "padding-footer-button1-top")
 */
export const getPaddingTestId = (sectionName: string, fieldName: string, side: string): string => {
  return `padding-${toKebabCase(sectionName)}-${toKebabCase(fieldName)}-${toKebabCase(side)}`;
};

/**
 * Generate test ID for input field element
 * @param sectionName - Section name
 * @param fieldName - Field name
 * @returns data-testid value (e.g., "input-header-back-button")
 */
export const getInputTestId = (sectionName: string, fieldName: string): string => {
  return `input-${toKebabCase(sectionName)}-${toKebabCase(fieldName)}`;
};

/**
 * Generate test ID for an expanded text-styles grid wrapper (the whole style group)
 * @param sectionName - Section name (e.g., "Action Bar")
 * @param fieldName - Field name (e.g., "Primary Button")
 * @returns data-testid value (e.g., "styles-group-action-bar-primary-button")
 */
export const getStyleGroupTestId = (sectionName: string, fieldName: string): string => {
  return `styles-group-${toKebabCase(sectionName)}-${toKebabCase(fieldName)}`;
};

/**
 * Generate test ID for style field input element
 * @param fieldName - Parent field name
 * @param styleName - Style property name (e.g., "fontSize", "color")
 * @returns data-testid value (e.g., "input-style-back-button-font-size")
 */
export const getStyleInputTestId = (fieldName: string, styleName: string): string => {
  return `input-style-${toKebabCase(fieldName)}-${toKebabCase(styleName)}`;
};

/**
 * Generate test ID for style field checkbox element
 * @param sectionId - Section ID (e.g., "unselectedItemState", "titleStyles")
 * @param fieldLabel - Field label (e.g., "background color", "font family")
 * @returns data-testid value (e.g., "checkbox-style-unselected-item-state-background-color")
 */
export const getStyleCheckboxTestId = (sectionId: string, fieldLabel: string): string => {
  return `checkbox-style-${toKebabCase(sectionId)}-${toKebabCase(fieldLabel)}`;
};

/**
 * Generate test ID for list item
 * @param sectionName - Section name (e.g., "List", "Slider")
 * @param itemIndex - Item index
 * @returns data-testid value (e.g., "list-item-0")
 */
export const getListItemTestId = (sectionName: string, itemIndex: number): string => {
  return `${toKebabCase(sectionName)}-item-${itemIndex}`;
};

/**
 * Generate test ID for add/remove item buttons
 * @param sectionName - Section name
 * @param action - Action (add, remove, move-up, move-down)
 * @param itemIndex - Optional item index for remove/move actions
 * @returns data-testid value (e.g., "btn-list-add", "btn-list-remove-0")
 */
export const getListActionTestId = (sectionName: string, action: string, itemIndex?: number): string => {
  const baseId = `btn-${toKebabCase(sectionName)}-${toKebabCase(action)}`;
  return itemIndex !== undefined ? `${baseId}-${itemIndex}` : baseId;
};

/**
 * Generate test ID for upload button icon
 * @param sectionName - Section name
 * @param fieldName - Field name
 * @returns data-testid value (e.g., "upload-btn-body-image")
 */
export const getUploadButtonTestId = (sectionName: string, fieldName: string): string => {
  return `upload-btn-${toKebabCase(sectionName)}-${toKebabCase(fieldName)}`;
};

/**
 * Generate test ID for image/video preview element
 * @param sectionName - Section name
 * @param fieldName - Field name
 * @returns data-testid value (e.g., "preview-body-image")
 */
export const getPreviewTestId = (sectionName: string, fieldName: string): string => {
  return `preview-${toKebabCase(sectionName)}-${toKebabCase(fieldName)}`;
};

/**
 * Generate test ID for slider element
 * @param sectionName - Section name
 * @param fieldName - Field name
 * @returns data-testid value (e.g., "slider-header-page-indicator")
 */
export const getSliderTestId = (sectionName: string, fieldName: string): string => {
  return `slider-${toKebabCase(sectionName)}-${toKebabCase(fieldName)}`;
};
